import React from "react"

import {i18n} from "../../../i18n.ts"
import {useSaaSCompanyOnboardingStatusQuery} from "../../../queries/saasCompany.ts"
import {useSaasUserManagementMetaQuery} from "../../../queries/saasUserManagement.ts"
import {useUserSettingsQuery} from "../../../queries/user.ts"
import {AUserTypes} from "../../../services/types.generated.ts"
import {isSaaSCompanyUser, isSaaSSalespersonUser, isSalesPersonUser} from "../../../utils/types.ts"

export const UserTypes = {...AUserTypes, Anonymous: "Anonymous"} as const

type TNavigationItem = {
  name: string
  href: string
  isExternal?: boolean
}

export const useNavigationItems = (): readonly TNavigationItem[] => {
  const {data, isPending} = useUserSettingsQuery()

  const user = data?.user
  const isSaaSCompanyUserOnboarding = user ? isSaaSCompanyUser(user) && user.enabled_onboarding_screen : false

  const saasUserManagementQuery = useSaasUserManagementMetaQuery()
  const {data: onboardingStatus} = useSaaSCompanyOnboardingStatusQuery({enabled: isSaaSCompanyUserOnboarding})

  const withNullish = React.useMemo<Array<null | false | undefined | TNavigationItem>>(() => {
    if (isPending) {
      return []
    }

    switch (user?.type) {
      case AUserTypes.Admin:
        return [
          {name: i18n.t("T_Sales plans"), href: "/admin/sellings"},
          {name: i18n.t("T_Matching"), href: "/admin/matching"},
          {name: i18n.t("Leads_HeaderItem"), href: "/leads"},
        ]
      case AUserTypes.CompanyUser:
        return [
          {
            name: i18n.t("T_Dashboard"),
            href: "/dashboard",
          },
          {
            name: i18n.t("T_How it works"),
            href: "/how-it-works",
          },
          data?.reach_engine_enabled && {name: i18n.t("ReachEngine_NavItem"), href: "/reach-engine"},
        ]
      case AUserTypes.SalesPerson:
        if (!isSalesPersonUser(user)) {
          return []
        }

        return [
          {
            name: i18n.t("T_Dashboard"),
            href: "/dashboard",
          },
          user.leads_enabled && {name: i18n.t("Leads_HeaderItem"), href: "/leads"},
        ]
      case UserTypes.SaasSalesPerson: {
        return [
          {
            name: i18n.t("T_Dashboard"),
            href: "/dashboard",
          },
          isSaaSSalespersonUser(user) &&
            user.sales_assistant_connection &&
            user.enabled_sales_assistant_screen && {
              name: i18n.t("T_SalesAssistant"),
              href: "/sales-assistant",
            },
        ]
      }
      case AUserTypes.SaasCompanyUser: {
        const isOnboardingComplete =
          onboardingStatus?.managed_prospects &&
          onboardingStatus?.used_klara_ai &&
          onboardingStatus?.visited_sales_intelligence
        return [
          isSaaSCompanyUserOnboarding &&
            !isOnboardingComplete && {
              name: i18n.t("T_Onboarding"),
              href: "/onboarding",
            },
          {
            name: i18n.t("T_SalesIntelligence"),
            href: "/dashboard",
          },
          isSaaSCompanyUser(user) &&
            user.enabled_sales_assistant_screen && {
              name: i18n.t("T_SalesAssistant"),
              href: "/sales-assistant",
            },
          saasUserManagementQuery.data && {name: i18n.t("SaaSUserManagement_HeaderTitle"), href: "/user-management"},
          data?.reach_engine_enabled && {name: i18n.t("ReachEngine_NavItem"), href: "/reach-engine"},
        ]
      }

      default:
        return []
    }
  }, [
    isPending,
    saasUserManagementQuery.data,
    user,
    onboardingStatus,
    isSaaSCompanyUserOnboarding,
    data?.reach_engine_enabled,
  ])

  return withNullish.filter(item => !!item)
}
